/* Ex Sidere — the ring in 3D, free rotation on every axis.
   Geometry comes from the product's 3D media in Shopify (GLB made from the
   original STEP files). Rotation is a virtual trackball: dragging
   turns the ring about the screen axes, so any orientation can be reached;
   it keeps a little inertia and settles. */
import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const meshCache = new Map();

// The ring comes from the product's own 3D media (GLB uploaded in Shopify admin).
async function loadMesh(url) {
  if (meshCache.has(url)) return meshCache.get(url);
  const pr = new GLTFLoader().loadAsync(url).then((gltf) => {
    const geos = [];
    gltf.scene.updateMatrixWorld(true);
    gltf.scene.traverse((o) => { if (o.isMesh) { const g = o.geometry.clone(); g.applyMatrix4(o.matrixWorld); geos.push(g); } });
    if (!geos.length) throw new Error('no mesh in model');
    const g = geos[0];
    g.computeBoundingBox(); const c = new THREE.Vector3(); g.boundingBox.getCenter(c); g.translate(-c.x, -c.y, -c.z);
    if (!g.attributes.normal) g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  });
  meshCache.set(url, pr);
  return pr;
}

function mount(stage, url, opt = {}) {
  const canvas = document.createElement('canvas');
  canvas.className = 'ring3d';
  canvas.setAttribute('role', 'img');
  canvas.setAttribute('aria-label', opt.label || 'The ring in 3D');
  stage.appendChild(canvas);

  let renderer;
  try { renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' }); }
  catch (e) { canvas.remove(); return null; }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  const key = new THREE.DirectionalLight(0xffffff, 1.1); key.position.set(-2, 3, 4); scene.add(key);
  const rim = new THREE.DirectionalLight(0xdfe6ee, 0.6); rim.position.set(3, -1, -3); scene.add(rim);

  const camera = new THREE.PerspectiveCamera(28, 1, 0.001, 1000);
  const pivot = new THREE.Group(); scene.add(pivot);
  // satin titanium, slightly cool like the renders
  const material = new THREE.MeshPhysicalMaterial({ color: 0xb7bcc1, metalness: 1, roughness: 0.52, envMapIntensity: 1.25 });
  let mesh = null, radius = 0.015;

  // a pleasing three-quarter view to start from
  const home = new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.55, 0.65, 0.12));
  pivot.quaternion.copy(home);

  let W = 0, H = 0, raf = 0, alive = true, idle = !REDUCED, vx = 0, vy = 0, dragging = false, lx = 0, ly = 0, lt = 0, zoom = 1, zoomT = 1;
  const resize = () => {
    W = stage.clientWidth; H = stage.clientHeight; if (!W || !H) return;
    renderer.setSize(W, H, false); camera.aspect = W / H;
    fit(); camera.updateProjectionMatrix(); kick();
  };
  const fit = () => {
    const fov = THREE.MathUtils.degToRad(camera.fov), fitH = radius / Math.sin(fov / 2), fitW = radius / Math.sin(Math.atan(Math.tan(fov / 2) * camera.aspect));
    camera.position.set(0, 0, Math.max(fitH, fitW) * 1.08 / zoom); camera.lookAt(0, 0, 0);
  };

  const axis = new THREE.Vector3(), dq = new THREE.Quaternion();
  const turn = (dx, dy) => {
    const a = Math.hypot(dx, dy); if (a < 1e-6) return;
    axis.set(dy / a, dx / a, 0);
    dq.setFromAxisAngle(axis, a);
    pivot.quaternion.premultiply(dq);
  };

  function frame(t) {
    raf = 0; if (!alive) return;
    let moving = false;
    if (!dragging) {
      if (Math.abs(vx) > 1e-5 || Math.abs(vy) > 1e-5) { turn(vx, vy); vx *= 0.94; vy *= 0.94; moving = true; }
      else if (idle) { turn(0.0022, 0); moving = true; }
    }
    if (Math.abs(zoomT - zoom) > 1e-3) { zoom += (zoomT - zoom) * 0.15; fit(); moving = true; }
    renderer.render(scene, camera);
    if (moving || dragging) kick();
  }
  function kick() { if (!raf && alive) raf = requestAnimationFrame(frame); }

  const stopIdle = () => { if (idle) { idle = false; opt.onTouch && opt.onTouch(); } };
  const S = () => 2.6 / Math.min(W, H); // radians per pixel
  canvas.addEventListener('pointerdown', (e) => {
    dragging = true; stopIdle(); lx = e.clientX; ly = e.clientY; lt = performance.now(); vx = vy = 0;
    canvas.setPointerCapture(e.pointerId); stage.classList.add('dragging'); kick();
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const dx = (e.clientX - lx) * S(), dy = (e.clientY - ly) * S(), now = performance.now(), dt = Math.max(1, now - lt);
    turn(dx, dy); vx = dx * Math.min(1, 16 / dt); vy = dy * Math.min(1, 16 / dt);
    lx = e.clientX; ly = e.clientY; lt = now; kick();
  });
  const end = () => { dragging = false; stage.classList.remove('dragging'); kick(); };
  canvas.addEventListener('pointerup', end); canvas.addEventListener('pointercancel', end);
  canvas.addEventListener('dblclick', () => api.reset());

  const api = {
    reset: () => { stopIdle(); vx = vy = 0; const from = pivot.quaternion.clone(); let k = 0;
      const step = () => { k = Math.min(1, k + 0.06); pivot.quaternion.slerpQuaternions(from, home, 1 - Math.pow(1 - k, 3)); renderer.render(scene, camera); if (k < 1) requestAnimationFrame(step); };
      step(); zoomT = 1; kick(); },
    nudge: (dx, dy) => { stopIdle(); turn(dx, dy); kick(); },
    zoom: (f) => { stopIdle(); zoomT = Math.max(0.8, Math.min(2.2, zoomT * f)); kick(); },
    destroy: () => { alive = false; ro.disconnect(); cancelAnimationFrame(raf); renderer.dispose(); material.dispose(); canvas.remove(); }
  };

  const ro = new ResizeObserver(resize);
  loadMesh(url).then((g) => {
    if (!alive) return;
    mesh = new THREE.Mesh(g, material); pivot.add(mesh);
    radius = g.boundingSphere.radius; camera.near = radius * 0.05; camera.far = radius * 200; camera.updateProjectionMatrix();
    ro.observe(stage); resize();
    opt.onReady && opt.onReady(api);
  }).catch((e) => { opt.onFail && opt.onFail(e); api.destroy(); });

  return api;
}

window.Ring3D = { mount, loadMesh };
window.dispatchEvent(new Event('ring3d-ready'));
